/**
 * Disclosure Dashboard Types
 *
 * Type definitions for the Disclosure Dashboard feature.
 * Tracks ITOP (Intent to Proceed) and disclosure signature status.
 *
 * Note: v1 scope is loan-level disclosure data only (dates/statuses).
 * Signer-level tracking would require additional data sources.
 */

// ============================================
// Disclosure Loan Row
// ============================================

/**
 * Loan data row for the disclosure dashboard
 * Uses actual DB column names from encompass_loans table
 */
export interface DisclosureLoanRow {
  id: string;
  encompass_loan_id: string;
  trans_details_loan_number: string;
  borrower_first_name: string;
  borrower_last_name: string;
  co_borrower_first_name: string | null;
  co_borrower_last_name: string | null;
  trans_details_loan_amt: number | null;
  trans_details_loan_purpose: string | null;
  property_address: string | null;
  subject_property_city: string | null;
  subject_property_state: string | null;
  milestone: string | null;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  channel: string | null;
  creation_date: string | null;
  application_date: string | null;

  // Rate lock status (values: 'Locked', 'NotLocked', 'Expired', null)
  rate_lock_status: string | null;
  // Rate lock expiration date
  rate_lock_buy_side_lock_expires: string | null;

  // File lock status (when someone has the loan open in Encompass Smart Client)
  is_file_locked: boolean | null;
  file_locked_by: string | null;
  file_locked_by_name: string | null;
  file_lock_time: string | null;
  file_lock_type: string | null;

  // Disclosure audit fields (for pre-validation)
  trans_details_interest_rate: number | null;
  loan_term_months: number | null;
  trans_details_term_due_in: number | null;
  first_payment_date: string | null;
  loan_originator_name: string | null;
  nmls_loan_originator_id: string | null;
  fees_interest_from_date: string | null;

  // Disclosure tracking fields
  le_itop_sent_date: string | null;
  le_itop_received_date: string | null;
  le_initial_sent_date: string | null;
  le_initial_received_date: string | null;
  le_redisclosure_sent_date: string | null;
  le_redisclosure_received_date: string | null;
  cd_sent_date: string | null;
  cd_received_date: string | null;
  cd_revised_sent_date: string | null;
  cd_revised_received_date: string | null;
  econsent_date: string | null;
  borrower_econsent_status: string | null;
  coborrower_econsent_status: string | null;

  // Additional loan details for modal display
  amortization_type: string | null;
  interest_only_term_months: number | null;
  trans_details_loan_type: string | null;

  // Audit cache fields (for instant modal loading)
  disclosure_audit_id: string | null;
  disclosure_audit_result: Record<string, unknown> | null;
  disclosure_audit_package_type: string | null;
  disclosure_audit_plan_code_id: string | null;
  disclosure_audit_at: string | null;
  disclosure_fields_hash: string | null;
  disclosure_audit_fields_hash: string | null;

  // Document order cache fields
  disclosure_order_id: string | null;
  disclosure_order_status: string | null;
  disclosure_order_documents: Array<{
    id: string;
    name?: string;
    pdf?: { url?: string };
  }> | null;
  disclosure_order_parties: Array<{
    id: string;
    name?: string;
    email?: string;
    role?: string;
  }> | null;
  disclosure_preview_url: string | null;
  disclosure_preview_hash: string | null;
}

/**
 * Extended disclosure loan with computed fields
 */
export interface DisclosureLoan extends DisclosureLoanRow {
  /** Full borrower name (computed) */
  borrower_name: string;
  /** Full co-borrower name (computed, null if no co-borrower) */
  coborrower_name: string | null;
  /** Full loan officer name (computed) */
  loan_officer_name: string;
  /** Whether ITOP has been sent (le_itop_sent_date is not null) */
  has_itop: boolean;
  /** Whether initial LE has been sent */
  has_initial_le: boolean;
  /** Whether LE has been received back */
  le_received: boolean;
  /** Whether CD has been sent */
  has_cd: boolean;
  /** Whether CD has been received back */
  cd_received: boolean;
  /** Overall disclosure status */
  disclosure_status: DisclosureStatus;
  /** Consumer Connect URL for borrower signing (from disclosure_recipients) */
  consumer_link: string | null;
  /** TRID compliance: LE not sent within 3 business days of application */
  le_overdue: boolean;
  /** Days since application (if app date exists and LE not sent) */
  days_since_app: number | null;
  /** Whether this loan has a valid cached audit ready for instant modal loading */
  has_valid_audit_cache: boolean;
  /** Whether this loan has a valid cached document order */
  has_valid_order_cache: boolean;
}

// ============================================
// Disclosure Status
// ============================================

export type DisclosureStatus =
  | 'not_sent'      // LE not sent yet (within 3-day window)
  | 'le_overdue'    // LE not sent AND past 3 business days - TRID violation
  | 'itop_missing'  // LE sent but ITOP not received
  | 'itop_received' // ITOP received
  | 'cd_sent'       // CD sent, awaiting signature
  | 'complete';     // CD received, fully disclosed

// ============================================
// Filters
// ============================================

export interface DisclosureFilters {
  search: string;
  lockStatus: 'all' | 'Locked' | 'NotLocked' | 'Expired';
  itopStatus: 'all' | 'missing' | 'received' | 'not_applicable';
  disclosureStatus: 'all' | DisclosureStatus;
  loanOfficerId?: string;
}

export const DEFAULT_DISCLOSURE_FILTERS: DisclosureFilters = {
  search: '',
  lockStatus: 'all',
  itopStatus: 'all',
  disclosureStatus: 'all',
  loanOfficerId: undefined
};

// ============================================
// Stats
// ============================================

export interface DisclosureStats {
  /** Total active loans (excluding transferred/closed) */
  totalActiveLoans: number;
  /** Loans where LE was sent but ITOP not received */
  itopMissing: number;
  /** Initial LE packages sent this month (MTD) */
  initialPackagesMtd: number;
}

// ============================================
// Export Types
// ============================================

export interface DisclosureExportRow {
  'Loan Number': string;
  'Borrower': string;
  'Co-Borrower': string;
  'Loan Amount': string;
  'Property Address': string;
  'Loan Purpose': string;
  'Milestone': string;
  'Loan Officer': string;
  'App Date': string;
  'Lock Status': string;
  'Lock Expires': string;
  'eConsent': string;
  'ITOP': string;
  'LE Initial Sent': string;
  'LE Initial Received': string;
  'LE Revised Sent': string;
  'LE Revised Received': string;
  'CD Sent': string;
  'CD Received': string;
  'CD Revised Sent': string;
  'CD Revised Received': string;
  'Created': string;
}

// ============================================
// Sorting
// ============================================

/** Sortable columns in the disclosure table */
export type SortColumn =
  | 'trans_details_loan_number'
  | 'borrower_name'
  | 'milestone'
  | 'trans_details_loan_amt'
  | 'property_address'
  | 'trans_details_loan_purpose'
  | 'loan_officer_name'
  | 'application_date'
  | 'rate_lock_buy_side_lock_expires'
  | 'econsent_date'
  | 'le_itop_received_date'
  | 'le_initial_sent_date'
  | 'creation_date';

/** Sort direction */
export type SortDirection = 'asc' | 'desc';
